import React, { useState, useRef, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';

export default function PullToRefresh({ children }) {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const startY = useRef(0);
  const containerRef = useRef(null);
  
  const THRESHOLD = 80;
  const MAX_PULL = 120;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleTouchStart = (e) => {
      if (window.scrollY === 0) {
        startY.current = e.touches[0].clientY;
      }
    };

    const handleTouchMove = (e) => {
      if (window.scrollY > 0 || isRefreshing) return;
      
      const currentY = e.touches[0].clientY;
      const diff = currentY - startY.current;
      
      if (diff > 0 && startY.current > 0) {
        // Apply resistance to pull
        const resistance = 0.4;
        const pull = Math.min(diff * resistance, MAX_PULL);
        setPullDistance(pull);
      }
    };

    const handleTouchEnd = () => {
      if (pullDistance >= THRESHOLD && !isRefreshing) {
        setIsRefreshing(true);
        setPullDistance(60);
        
        // Perform refresh
        setTimeout(() => {
          window.location.reload();
        }, 300);
      } else {
        setPullDistance(0);
      }
      startY.current = 0;
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: true });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [pullDistance, isRefreshing]);

  const progress = Math.min(pullDistance / THRESHOLD, 1);
  const rotation = progress * 360;

  return (
    <div ref={containerRef} className="relative min-h-screen">
      {/* Pull indicator */}
      <div 
        className="fixed left-1/2 -translate-x-1/2 z-[100] flex items-center justify-center pointer-events-none"
        style={{ 
          top: pullDistance - 50,
          opacity: pullDistance > 10 ? 1 : 0,
          transition: pullDistance === 0 ? 'all 0.3s ease' : 'opacity 0.1s'
        }}
      >
        <motion.div 
          className={`w-10 h-10 rounded-full bg-slate-800 border border-slate-600 flex items-center justify-center shadow-lg ${isRefreshing ? 'animate-spin' : ''}`}
          style={{ rotate: isRefreshing ? undefined : rotation }}
        >
          <RefreshCw className={`w-5 h-5 ${progress >= 1 || isRefreshing ? 'text-cyan-400' : 'text-slate-400'}`} />
        </motion.div>
      </div>
      
      {/* Content with transform for pull effect */}
      <div 
        style={{ 
          transform: `translateY(${pullDistance}px)`,
          transition: pullDistance === 0 ? 'transform 0.3s ease' : 'none'
        }}
      >
        {children}
      </div>
    </div>
  );
}